import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, X, Loader2, AlertTriangle, ShieldCheck, Download, Trash2, Upload,
  FileText, ChevronUp, ChevronDown, ClipboardList, History, Sparkles,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { useReturnTo } from "@/lib/returnTo";
import { Card, Pill, Button, Input, Select, Textarea, Spinner, EmptyState } from "@/components/ui";
import {
  type MopInputs, type MopRefKind, emptyMopInputs, computeFlags,
  CRANE_SETUP_WORDING, SUSPENDED_LOADS_WORDING, PAINTED_STRUCTURE_WORDING,
  craneSeedSteps, isUntouchedCraneSeed, PRESTART_GENERIC, PRESTART_CRANE,
  CORETEL_STANDING_WIND_MS, CORETEL_STANDING_WIND_WORDING,
} from "./mopTemplate";
import { exportMopPdf } from "./mopPdf";
import { HSE_EVIDENCE_BUCKET, signedEvidenceUrl } from "./evidenceUpload";
import { isBackbone, parseBackbone, blockKey } from "./mopBackbone";

// =============================================================================
// MOP builder — T-MOP (James, 10 Sep 2026)
// =============================================================================
// "I need to build a MOP for Monday's high risk lift… maybe we could build a
// MOP generator, I put in some steps, we add some snips / HSE risk reviews
// kind of like the SWMS we already have… with maybe a google earth overlay."
//
// Built after the Beechmont pole-stand MOP was drafted by hand (Rev 0/0.1)
// and the scribe review set the rules — mopTemplate.ts carries them. The
// shape mirrors lift plans exactly: mops row + inputs jsonb, save_mop /
// approve_mop RPCs (mig 362), director approval snapshots to mop_versions,
// PDF regenerated client-side from the frozen inputs (mopPdf.ts) so an old
// revision re-downloads byte-equivalent any time.
//
// The one thing this screen must never do: put a fact in the document the
// manager didn't put in the form. Free-text professionalising is a WORKER
// job (next piece, /swms/ai-draft pattern) and even that only cleans
// language — it never adds technical content.
// =============================================================================

// 18 Sep 2026 (mig 380) — a MOP can start on a BID: the New MOP modal has a
// Project / BID switch, the register and editor show whichever parent the row
// has, and ?bid=<id> opens the modal preset (the BID page's "New MOP"). The
// carry-over to the project happens in the database when the BID is won.

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function MopBuilderScreen() {
  const { mopId } = useParams<{ mopId: string }>();
  return mopId ? <MopEditor id={mopId}/> : <MopList/>;
}

// -----------------------------------------------------------------------------
// Register
// -----------------------------------------------------------------------------
function MopList() {
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const presetBid = searchParams.get("bid");
  const presetProject = searchParams.get("project");
  const [adding, setAdding] = useState(!!(presetBid || presetProject));

  // Delete straight from the register — James, 11 Sep 2026: "put it in the
  // menu". Only ever offered on a draft that has never been approved
  // (version 0); app.delete_mop (mig 364) enforces the same server-side.
  async function deleteDraft(m: any) {
    if (!window.confirm(
      `Delete "${m.title}" (${m.mop_number})?\n\nThis draft was never approved, so nothing references it. This can't be undone.`,
    )) return;
    const { error } = await (supabase as any).schema("app").rpc("delete_mop", { p_mop_id: m.mop_id });
    if (error) { window.alert(`Delete failed: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["mops"] });
  }

  const { data: mops = [], isLoading } = useQuery({
    queryKey: ["mops"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("mops")
        // mig 380 — a MOP is on a project OR a bid; show whichever it has
        .select("mop_id, mop_number, title, status, version, approved_at, created_at, projects:project_id(project_code, project_name, sites:site_id(site_name)), bids:bid_id(bid_number, bid_title, sites:site_id(site_name))")
        .neq("status", "superseded")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">HSE</div>
          <h1 className="text-2xl font-semibold tracking-tight">MOP builder</h1>
          <p className="text-text-muted text-[13px] mt-1 max-w-[560px]">
            Methods of Procedure as project records — structured inputs in, a controlled
            branded document out. Drafts are not for use until a director approves; every
            approved revision stays re-downloadable.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/mops"><Button variant="secondary">MOP template library</Button></Link>
          {canEdit && <Button icon={Plus} onClick={() => setAdding(true)}>New MOP</Button>}
        </div>
      </div>

      {isLoading && <div className="flex justify-center py-10"><Spinner size={18}/></div>}
      {!isLoading && mops.length === 0 && (
        <EmptyState icon={ClipboardList} title="No MOPs yet"
          description="Start one against a project — attach the lift study, put in your steps, generate the document."/>
      )}

      {mops.length > 0 && (
        <Card>
          <div className="divide-y divide-line/40">
            {mops.map(m => (
              <div key={m.mop_id} role="button" tabIndex={0}
                onClick={() => navigate(`/hse/mops/${m.mop_id}`)}
                onKeyDown={e => { if (e.key === "Enter") navigate(`/hse/mops/${m.mop_id}`); }}
                className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap hover:bg-ink-raised/40 transition cursor-pointer">
                <span className="font-mono text-[12px] text-accent w-[104px]">{m.mop_number}</span>
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[13px]">{m.title}</div>
                  <div className="text-[11px] text-text-muted">
                    {m.projects
                      ? <>{m.projects.project_code}{m.projects.sites?.site_name ? ` · ${m.projects.sites.site_name}` : ""}</>
                      : m.bids
                        ? <><span className="text-accent">BID</span> {m.bids.bid_number}{m.bids.sites?.site_name ? ` · ${m.bids.sites.site_name}` : ` · ${m.bids.bid_title ?? ""}`}</>
                        : ""}
                  </div>
                </div>
                {m.status === "approved"
                  ? <Pill label={`approved v${m.version}`} tone="ok" small/>
                  : <Pill label="DRAFT — NOT FOR USE" tone="warning" small/>}
                {canEdit && m.status === "draft" && (m.version ?? 0) === 0 && (
                  <button type="button" title="Delete this never-approved draft"
                    onClick={e => { e.stopPropagation(); void deleteDraft(m); }}
                    className="p-1.5 rounded text-text-dim hover:text-danger hover:bg-danger/10 transition-colors">
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {adding && <NewMopModal onClose={() => setAdding(false)} presetBid={presetBid} presetProject={presetProject}
        onCreated={id => { setAdding(false); navigate(`/hse/mops/${id}`); }}/>}
    </div>
  );
}

function NewMopModal({ onClose, onCreated, presetBid = null, presetProject = null }: {
  onClose: () => void; onCreated: (id: string) => void; presetBid?: string | null; presetProject?: string | null;
}) {
  // mig 380 — a MOP starts on a project OR a BID (James, 18 Sep: "MOPs on
  // BIDs not just Projects"). It moves to the project by itself when the BID
  // is won.
  const [parent, setParent] = useState<"project" | "bid">(presetBid ? "bid" : "project");
  const [projectId, setProjectId] = useState(presetProject ?? "");
  const [bidId, setBidId] = useState(presetBid ?? "");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: projects = [] } = useQuery({
    queryKey: ["mop_projects"],
    queryFn: async () => {
      const { data } = await supabase.from("projects")
        .select("project_id, project_code, project_name, sites:site_id(site_name)")
        .in("status", ["scheduled", "won", "mobilising", "live", "on_hold", "acceptance_pending"])
        .order("project_code", { ascending: false });
      return (data ?? []) as any[];
    },
  });
  const { data: bids = [] } = useQuery({
    queryKey: ["mop_bids"],
    queryFn: async () => {
      const { data } = await supabase.from("bids")
        .select("bid_id, bid_number, bid_title, sites:site_id(site_name)")
        .in("status", ["open", "responded"])
        .order("bid_number", { ascending: false });
      return (data ?? []) as any[];
    },
  });
  const chosenProject = projects.find(p => p.project_id === projectId);
  const chosenBid = (bids as any[]).find(b => b.bid_id === bidId);
  const ready = parent === "project" ? !!projectId : !!bidId;

  async function create() {
    setBusy(true); setErr(null);
    const fallbackSite = parent === "project"
      ? (chosenProject?.sites?.site_name ?? chosenProject?.project_name ?? "")
      : (chosenBid?.sites?.site_name ?? chosenBid?.bid_title ?? "");
    const { data, error } = await (supabase as any).schema("app").rpc("save_mop", {
      p_mop_id: null,
      p_project_id: parent === "project" ? projectId : null,
      p_bid_id: parent === "bid" ? bidId : null,
      p_title: title.trim() || `MOP — ${fallbackSite}`,
      p_inputs: emptyMopInputs(todayKey()),
      p_crane_config_id: null,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onCreated(data as string);
  }

  const tab = (v: "project" | "bid", label: string) => (
    <button type="button" onClick={() => setParent(v)}
      className={`px-3 py-1.5 rounded-md text-[12px] border transition-colors ${parent === v ? "border-accent bg-accent/10 text-text" : "border-line text-text-muted hover:border-line-light"}`}>
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-[10vh]" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[520px]" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">New MOP</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-text-muted">For a</span>
            {tab("project", "Project")}
            {tab("bid", "BID (not yet won)")}
          </div>
          {parent === "project" ? (
            <label className="block text-[12px] text-text-muted">Project *
              <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
                <option value="">Pick…</option>
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_code} · {p.sites?.site_name ?? p.project_name}
                  </option>
                ))}
              </Select>
            </label>
          ) : (
            <label className="block text-[12px] text-text-muted">BID *
              <Select value={bidId} onChange={e => setBidId(e.target.value)}>
                <option value="">Pick…</option>
                {(bids as any[]).map(b => (
                  <option key={b.bid_id} value={b.bid_id}>
                    {b.bid_number} · {b.sites?.site_name ?? b.bid_title}
                  </option>
                ))}
              </Select>
            </label>
          )}
          <label className="block text-[12px] text-text-muted">Title
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Pole stand — 40m monopole erection"/>
          </label>
          <div className="text-[11px] text-text-muted">
            Starts as a draft with the standard pre-lift steps seeded — edit or remove any of them.
            {parent === "bid" && " A BID MOP is managers-only until the BID is won; it then moves onto the project by itself."}
          </div>
          <Button onClick={create} disabled={busy || !ready} className="w-full justify-center">
            {busy ? <Loader2 size={14} className="animate-spin"/> : "Create MOP"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Editor
// -----------------------------------------------------------------------------
function MopEditor({ id }: { id: string }) {
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");
  const isDirector = hasRole(user, "director");
  const returnTo = useReturnTo();   // 27 Sep — opened from the field job page? go back there
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [inputs, setInputs] = useState<MopInputs | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState<null | "save" | "approve" | "pdf" | "upload" | "tidy" | "extract" | "delete">(null);
  const [extractRes, setExtractRes] = useState<{ proposals: any; file_name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const hydratedFor = useRef<string | null>(null);
  const flagsCardRef = useRef<HTMLDivElement | null>(null);

  const { data: row } = useQuery({
    queryKey: ["mop", id],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("mops")
        .select("*, projects:project_id(project_code, project_name, clients:client_id(legal_name), sites:site_id(site_name, address_line1, suburb, state)), bids:bid_id(bid_number, bid_title, clients:client_id(legal_name), sites:site_id(site_name, address_line1, suburb, state)), approver:approved_by_user_id(login_email, employees:employee_id(first_name, last_name))")
        .eq("mop_id", id).single();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: versions = [] } = useQuery({
    queryKey: ["mop_versions", id],
    queryFn: async () => {
      const { data } = await (supabase as any).from("mop_versions")
        .select("version, inputs, approved_at, approver:approved_by_user_id(login_email, employees:employee_id(first_name, last_name))")
        .eq("mop_id", id).order("version", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // Configs for the crane picker. All of them, not just verified — the MOP
  // references the LIFT STUDY for capacities; the library entry is identity
  // and wind limit. A draft chart still shows its badge so nobody's misled.
  const { data: configs = [] } = useQuery({
    queryKey: ["crane_configs_all"],
    queryFn: async () => {
      const { data } = await supabase.from("crane_configs")
        .select("*, crane_models(make, model, capacity_class_t)")
        .order("config_name");
      return (data ?? []) as any[];
    },
  });

  // Hydrate local inputs from the row exactly once per mop id.
  useEffect(() => {
    if (row && hydratedFor.current !== id) {
      hydratedFor.current = id;
      const base = emptyMopInputs(todayKey());
      const stored = (row.inputs ?? {}) as Partial<MopInputs>;
      setInputs({
        ...base, ...stored,
        meta: { ...base.meta, ...(stored.meta ?? {}) },
        environmental: { ...base.environmental, ...(stored.environmental ?? {}) },
        flags: { ...base.flags, ...(stored.flags ?? {}) },
      });
      setDirty(false);
    }
  }, [row, id]);

  const flags = useMemo(() => inputs ? computeFlags(inputs) : [], [inputs]);
  const blocking = flags.filter(f => f.blocking);
  const advisories = flags.filter(f => !f.blocking);
  const unacked = advisories.filter(f => !inputs?.flags.acknowledged.includes(f.id));

  // Autosave — "what does save draft do?" (James, 11/09) shouldn't be a
  // question anyone has to ask. Debounced 2.5s after the last change; skipped
  // on APPROVED MOPs so a stray keystroke can't silently demote one to draft
  // (editing an approved MOP stays a deliberate act: type, then press Save).
  const rowStatus = (row as any)?.status;
  useEffect(() => {
    if (!dirty || !row || rowStatus === "approved" || !canEdit || busy != null) return;
    const t = setTimeout(() => { void save(); }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, dirty, rowStatus, busy, canEdit]);

  if (!row || !inputs) return <div className="flex justify-center py-16"><Spinner size={18}/></div>;

  // mig 380 — the MOP's parent is a project or a BID; the header and the PDF
  // cover read the same fields off whichever it has.
  const parent = row.projects
    ? { code: row.projects.project_code, name: row.projects.project_name, sites: row.projects.sites, clients: row.projects.clients, isBid: false }
    : row.bids
      ? { code: row.bids.bid_number, name: row.bids.bid_title, sites: row.bids.sites, clients: row.bids.clients, isBid: true }
      : { code: undefined, name: undefined, sites: undefined, clients: undefined, isBid: false };

  const inp = inputs;
  const patch = (next: Partial<MopInputs>) => { setInputs({ ...inp, ...next }); setDirty(true); };
  const approved = row.status === "approved" && !dirty;

  async function save(): Promise<boolean> {
    setBusy("save"); setErr(null);
    const { error } = await (supabase as any).schema("app").rpc("save_mop", {
      p_mop_id: id,
      p_project_id: row.project_id,
      p_bid_id: row.bid_id ?? null,
      p_title: row.title,
      p_inputs: inp,
      p_crane_config_id: inp.crane?.crane_config_id ?? null,
    });
    setBusy(null);
    if (error) { setErr(error.message); return false; }
    setDirty(false); setSaved(true); setTimeout(() => setSaved(false), 2200);
    qc.invalidateQueries({ queryKey: ["mop", id] });
    return true;
  }

  async function approve() {
    if (blocking.length > 0) {
      window.alert("Fix the blocking items in 'Before you generate' first — an approval freezes this content.");
      return;
    }
    if (!window.confirm(
      "Approve this MOP?\n\nYou're confirming the method, references and controls have been reviewed by a competent person and the content is fit to work to. The approved revision is frozen and stays re-downloadable.",
    )) return;
    setBusy("approve"); setErr(null);
    if (dirty && !(await save())) { setBusy(null); return; }
    const { error } = await (supabase as any).schema("app").rpc("approve_mop", { p_mop_id: id });
    setBusy(null);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["mop", id] });
    qc.invalidateQueries({ queryKey: ["mop_versions", id] });
  }

  // Delete a draft that never earned an approval. Server-side (mig 364)
  // app.delete_mop refuses anything with version > 0 or mop_versions rows —
  // same discipline as the crane library: an approved document is history and
  // history never gets erased. James, 11 Sep 2026, first live dress rehearsal:
  // "How do we delete? I think we should just re-do a new MOP."
  async function deleteDraft() {
    if (!window.confirm(
      `Delete "${row.title}"?\n\nThis draft was never approved, so nothing references it. This can't be undone.`,
    )) return;
    setBusy("delete"); setErr(null);
    const { error } = await (supabase as any).schema("app").rpc("delete_mop", { p_mop_id: id });
    setBusy(null);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["mops"] });
    navigate("/hse/mops");
  }

  async function dataUrlFor(path: string): Promise<string | null> {
    try {
      const url = await signedEvidenceUrl(path);
      if (!url) return null;
      const blob = await (await fetch(url)).blob();
      return await new Promise(res => {
        const r = new FileReader();
        r.onload = () => res(String(r.result));
        r.onerror = () => res(null);
        r.readAsDataURL(blob);
      });
    } catch { return null; }
  }

  async function pdf(fromVersion?: { version: number; inputs: MopInputs; approvedAt?: string | null; approvedBy?: string | null }) {
    const useInputs = fromVersion?.inputs ?? inp;
    if (!fromVersion) {
      if (blocking.length > 0) {
        window.alert("Blocked — resolve the items in 'Before you generate' first.");
        return;
      }
      if (unacked.length > 0) {
        window.alert("There are unacknowledged advisories in 'Before you generate'. Tick them (or fix them) first.");
        return;
      }
    }
    setBusy("pdf"); setErr(null);
    try {
      const layoutImageDataUrl = useInputs.layout_image ? await dataUrlFor(useInputs.layout_image) : null;
      const forecastImageDataUrl = useInputs.forecast_image ? await dataUrlFor(useInputs.forecast_image) : null;
      const stepImageDataUrls: Record<number, string> = {};
      for (let i = 0; i < useInputs.method.length; i++) {
        const p = useInputs.method[i].image_path;
        if (p) { const d = await dataUrlFor(p); if (d) stepImageDataUrls[i] = d; }
      }
      const approverName = row.approver
        ? ([row.approver.employees?.first_name, row.approver.employees?.last_name].filter(Boolean).join(" ").trim() || row.approver.login_email)
        : null;
      await exportMopPdf({
        mopNumber: row.mop_number,
        title: row.title,
        version: fromVersion?.version ?? row.version ?? 0,
        status: fromVersion ? "approved" : (dirty ? "draft" : row.status),
        projectCode: parent.code,
        projectName: parent.name,
        siteName: parent.sites?.site_name,
        // Site records sometimes carry the suburb/state inside address_line1
        // already ("Beechmont, QLD 4211") — only append parts not present yet,
        // so the cover never prints "…QLD 4211, QLD".
        siteAddress: [parent.sites?.address_line1, parent.sites?.suburb, parent.sites?.state]
          .map((p: any) => String(p ?? "").trim()).filter(Boolean)
          .reduce((acc: string[], p: string) => {
            if (!acc.some(x => x.toLowerCase().includes(p.toLowerCase()))) acc.push(p);
            return acc;
          }, [])
          .join(", "),
        clientName: parent.clients?.legal_name,
        preparedBy: [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.login_email || null,
        approvedBy: fromVersion?.approvedBy ?? approverName,
        approvedAt: fromVersion?.approvedAt ?? row.approved_at,
        versions: (versions as any[]).map(v => ({
          version: v.version,
          approved_at: v.approved_at,
          approved_by: v.approver
            ? ([v.approver.employees?.first_name, v.approver.employees?.last_name].filter(Boolean).join(" ").trim() || v.approver.login_email)
            : null,
        })),
        inputs: useInputs,
        layoutImageDataUrl,
        forecastImageDataUrl,
        stepImageDataUrls,
      });
    } catch (e: any) {
      window.alert(`PDF failed: ${e?.message ?? e}`);
    }
    setBusy(null);
  }

  // T-MOP piece 2 — the builder's one AI call. Sends the steps to the worker,
  // which cleans the LANGUAGE of each step's details and nothing else (scribe
  // rule: never adds technical facts). The tidied text lands as details_pro,
  // shown for review under each step; editing a step's details discards its
  // tidied version so the document can never print wording the manager
  // hasn't seen against their own.
  async function tidy() {
    if (inp.method.every(s => !s.details.trim())) {
      window.alert("No step details to tidy yet — write your steps first.");
      return;
    }
    // 23 Sep 2026 — James: "the tidy, not sure what that does". It is the one
    // AI call in the builder, and on steps that came out of a Projects Claude
    // input file it is the wrong tool: those are already written as field
    // instructions (WHO / DO / CHECK / STOP IF), and the worker returns them
    // as flowing prose — structure the crew reads at an anchor, gone. So when
    // the MOP carries document blocks (the signature of an extracted file) the
    // button says so first, rather than quietly rewriting someone's work.
    const fromDocument = (inp.blocks ?? []).length > 0;
    if (fromDocument && !window.confirm(
      "These steps look like they came from an input file.\n\n" +
      "Tidy rewrites each step's details into flowing prose. It never adds a technical fact, " +
      "but it does drop the WHO / DO / CHECK / STOP IF layout the file was written in.\n\n" +
      "Tidy anyway?"
    )) return;
    setBusy("tidy"); setErr(null);
    try {
      const res = await apiFetch("/mops/professionalise", {
        method: "POST",
        body: JSON.stringify({ steps: inp.method.map(s => ({ title: s.title, action: s.action, details: s.details })) }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({} as any)) as any;
        throw new Error(e?.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as any;
      const method = inp.method.map((s, i) => ({
        ...s,
        details_pro: s.details.trim() ? ((data.steps?.[i]?.details_pro as string | undefined) || undefined) : undefined,
      }));
      patch({ method });
    } catch (e: any) {
      setErr(`Tidy failed: ${e?.message ?? e}`);
    }
    setBusy(null);
  }

  // T-MOP piece 3 — extract-and-prefill. The worker reads the attached
  // document and returns PROPOSALS, each with a source quote; the review
  // modal shows them beside the current values and nothing lands in the MOP
  // without an explicit accept. Propose-and-dispose, never silent fill.
  async function extract(ref: { path: string; file_name: string; kind: string }) {
    setBusy("extract"); setErr(null);
    try {
      // 21 Sep 2026 — a MOP backbone written by the Projects Claude is read
      // here, deterministically, section by section (mopBackbone.ts). No AI:
      // the file is already laid out against the builder's fields. Anything
      // else (.md talk-through, lift study PDF, snip) still goes to the worker.
      if (/\.(md|markdown|txt)$/i.test(ref.file_name)) {
        const { data: blob, error } = await supabase.storage.from(HSE_EVIDENCE_BUCKET).download(ref.path);
        if (error) throw new Error(`Couldn't read ${ref.file_name}: ${error.message}`);
        const text = await blob.text();
        if (isBackbone(text)) {
          setExtractRes({ proposals: parseBackbone(text), file_name: ref.file_name, kind: "backbone" } as any);
          setBusy(null);
          return;
        }
      }
      const res = await apiFetch("/mops/extract", {
        method: "POST",
        body: JSON.stringify({ path: ref.path, file_name: ref.file_name, kind: ref.kind }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({} as any)) as any;
        throw new Error(e?.error ?? `HTTP ${res.status}`);
      }
      setExtractRes(await res.json() as any);
    } catch (e: any) {
      setErr(`Extract failed: ${e?.message ?? e}`);
    }
    setBusy(null);
  }

  async function uploadRef(file: File, kind: MopRefKind): Promise<string | null> {
    setBusy("upload"); setErr(null);
    const path = `mops/${id}/${crypto.randomUUID()}-${file.name.replace(/[^A-Za-z0-9._-]+/g, "_")}`;
    const { error } = await supabase.storage.from(HSE_EVIDENCE_BUCKET)
      .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
    setBusy(null);
    if (error) { setErr(`Upload failed: ${error.message}`); return null; }
    return path;
  }

  const cfgLabel = (c: any) =>
    `${c.crane_models?.make ?? ""} ${c.crane_models?.model ?? ""} — ${c.config_name}`;

  function pickCrane(configId: string) {
    if (!configId) {
      // No crane: drop the crane seed steps if nobody has typed in them, and
      // put the pre-start wording back to the generic line.
      const method = inp.method
        .filter(s => !isUntouchedCraneSeed(s))
        .map(s => s.title === "Pre-start briefing and sign-on" && s.action === PRESTART_CRANE ? { ...s, action: PRESTART_GENERIC } : s);
      patch({ crane: null, method, suspended_loads: false });
      return;
    }
    const c = configs.find(x => x.crane_config_id === configId);
    if (!c) return;
    // A crane: seed the three crane steps once (after the pre-start), and use
    // the lift-study pre-start wording if the generic one is still there.
    const hasCraneSteps = inp.method.some(s => s.title === "Crane setup per lift study");
    let method = inp.method.map(s => s.title === "Pre-start briefing and sign-on" && s.action === PRESTART_GENERIC ? { ...s, action: PRESTART_CRANE } : s);
    if (!hasCraneSteps) {
      const at = method.findIndex(s => s.title === "Pre-start briefing and sign-on");
      method = at >= 0 ? [...method.slice(0, at + 1), ...craneSeedSteps(), ...method.slice(at + 1)] : [...craneSeedSteps(), ...method];
    }
    patch({
      method,
      suspended_loads: true,
      crane: {
        crane_config_id: configId,
        model_label: `${c.crane_models?.make ?? ""} ${c.crane_models?.model ?? ""}`.trim(),
        config_label: c.config_name,
        counterweight_t: c.counterweight_t,
        outriggers: c.outrigger_width_m ? `${c.outrigger_width_m} × ${c.outrigger_length_m} m` : "",
        chart_wind_ms: c.chart_wind_limit_ms,
        chart_basis: c.chart_basis,
      },
      environmental: {
        ...inp.environmental,
        wind_limit_ms: inp.environmental.wind_limit_ms ?? c.chart_wind_limit_ms ?? null,
      },
    });
  }

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto pb-48 space-y-4">
      {/* ---- header ---- */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to={returnTo ?? "/hse/mops"} className="text-[12px] text-text-muted hover:text-text">{returnTo ? "← Back to the job" : "← MOP builder"}</Link>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="font-mono text-[13px] text-accent">{row.mop_number}</span>
            {approved
              ? <Pill label={`approved v${row.version}`} tone="ok"/>
              : <Pill label="DRAFT — NOT FOR USE" tone="warning"/>}
          </div>
          <h1 className="text-xl font-semibold tracking-tight mt-0.5">{row.title}</h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            {parent.isBid && <span className="text-accent mr-1">BID</span>}{parent.code} · {parent.sites?.site_name ?? parent.name}
            {parent.isBid && <span className="ml-2 text-text-dim">moves to the project when won</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {versions.length > 0 && (
            <Button variant="secondary" icon={History} onClick={() => setShowVersions(v => !v)}>
              Revisions ({versions.length})
            </Button>
          )}
          <Button variant="secondary" icon={Download} onClick={() => void pdf()} disabled={busy != null}>
            {busy === "pdf" ? <Loader2 size={14} className="animate-spin"/> : `PDF${approved ? "" : " (draft)"}`}
          </Button>
        </div>
      </div>

      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      {!approved && (
        <div className="bg-warn/10 border border-warn/40 rounded-md px-4 py-2.5 text-[12px] text-warn">
          <AlertTriangle size={12} className="inline mr-1.5 -mt-0.5"/>
          Draft — crews must not work to this until a director approves it. The crane company's
          lift study still governs the crane's own setup and capacities.
        </div>
      )}

      {showVersions && versions.length > 0 && (
        <Card>
          <div className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Approved revisions — frozen, re-downloadable</div>
            <div className="divide-y divide-line/40">
              {versions.map((v: any) => {
                const who = v.approver
                  ? ([v.approver.employees?.first_name, v.approver.employees?.last_name].filter(Boolean).join(" ").trim() || v.approver.login_email)
                  : "—";
                return (
                  <div key={v.version} className="py-2 flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[12px] text-accent">v{v.version}</span>
                    <span className="text-[12px] text-text-muted flex-1">approved by {who} · {v.approved_at ? new Date(v.approved_at).toLocaleDateString("en-AU") : "—"}</span>
                    <Button size="sm" variant="secondary" icon={Download} disabled={busy != null}
                      onClick={() => void pdf({ version: v.version, inputs: v.inputs as MopInputs, approvedAt: v.approved_at, approvedBy: who })}>
                      Download v{v.version}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* ---- job details ---- */}
      <Card>
        <div className="p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">1 · Job details</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block text-[12px] text-text-muted">Work date
              <Input type="date" value={inp.meta.mop_date} disabled={!canEdit}
                onChange={e => patch({ meta: { ...inp.meta, mop_date: e.target.value } })}/>
            </label>
            <label className="block text-[12px] text-text-muted md:col-span-2">Client chain
              <Input value={inp.meta.client_chain} disabled={!canEdit}
                placeholder={parent.clients?.legal_name ? `e.g. ${parent.clients.legal_name} → Coretel` : "e.g. NBN Co → Ventia → Coretel"}
                onChange={e => patch({ meta: { ...inp.meta, client_chain: e.target.value } })}/>
            </label>
          </div>
          <label className="block text-[12px] text-text-muted">Crew — one per line, name and role
            <Textarea rows={3} value={inp.meta.crew.join("\n")} disabled={!canEdit}
              placeholder={"J. Bloggs — Lift supervisor / dogger\nS. Smith — Rigger"}
              onChange={e => patch({ meta: { ...inp.meta, crew: e.target.value.split("\n").filter(l => l.trim() !== "") } })}/>
          </label>
        </div>
      </Card>

      {/* ---- crane & plant ---- */}
      <Card>
        <div className="p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">2 · Crane & plant — pick the crane from the library</div>
          <label className="block text-[12px] text-text-muted">Crane configuration — from the crane library
            <Select value={inp.crane?.crane_config_id ?? ""} disabled={!canEdit}
              onChange={e => pickCrane(e.target.value)}>
              <option value="">No crane on this MOP</option>
              {configs.map(c => (
                <option key={c.crane_config_id} value={c.crane_config_id}>{cfgLabel(c)}</option>
              ))}
            </Select>
          </label>
          {inp.crane && (
            <div className="bg-ink rounded-md border border-line/50 px-3 py-2.5 text-[12px] text-text-muted flex items-center gap-2 flex-wrap">
              <span className="text-text">{inp.crane.model_label}</span>
              {inp.crane.counterweight_t != null && <span>· {inp.crane.counterweight_t} t CW</span>}
              {inp.crane.outriggers && <span>· outriggers {inp.crane.outriggers}</span>}
              {inp.crane.chart_wind_ms != null && <span>· chart wind {inp.crane.chart_wind_ms} m/s</span>}
              <Pill label={inp.crane.chart_basis === "machine" ? "machine chart" : "generic chart"}
                tone={inp.crane.chart_basis === "machine" ? "ok" : "notice"} small/>
            </div>
          )}
          {/* Plant & equipment — every line editable here, including the ones an
              extract put in. James, 23 Sep 2026: "Need a way in Ops to edit this
              details and lines … where is the plant and equipment edit?" The rows
              were always editable, but the details sat in a ONE-LINE input, so a
              rigging line that runs to a paragraph (quantities per anchor, truck
              spares) showed about eight words of itself and could not be worked
              on. It is a box now, and the section says what it holds. */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">Plant &amp; equipment — quantities and detail</div>
              <div className="text-[11px] text-text-dim">
                {inp.plant.length === 0 ? "nothing listed yet" : `${inp.plant.length} line${inp.plant.length === 1 ? "" : "s"} — edit or delete any of them`}
              </div>
            </div>
            {/* A grid, not a flex row. Input and Textarea both carry w-full in
                their own base classes, so in a flex row the item input won the
                width and the details box collapsed to a sliver at the right
                edge — the details were there and editable, just invisible
                (James, 23 Sep: "I can't see the qty or details in the next text
                box"). The grid owns the columns, so neither can do that. */}
            {inp.plant.map((p, i) => (
              <div key={i} className="grid grid-cols-[minmax(140px,220px)_1fr_auto] gap-2 items-start">
                <Input value={p.item} placeholder="Rope grab (cable grip)" disabled={!canEdit}
                  onChange={e => { const plant = [...inp.plant]; plant[i] = { ...p, item: e.target.value }; patch({ plant }); }}/>
                <Textarea rows={2} value={p.details} disabled={!canEdit}
                  placeholder="Qty, sizes, spares on the truck, supplier, what it is for"
                  onChange={e => { const plant = [...inp.plant]; plant[i] = { ...p, details: e.target.value }; patch({ plant }); }}/>
                {canEdit && (
                  <button type="button" className="text-text-dim hover:text-danger mt-2"
                    onClick={() => patch({ plant: inp.plant.filter((_, x) => x !== i) })}><Trash2 size={13}/></button>
                )}
              </div>
            ))}
            {canEdit && (
              <Button size="sm" variant="secondary" icon={Plus}
                onClick={() => patch({ plant: [...inp.plant, { item: "", details: "" }] })}>Add plant line</Button>
            )}
          </div>
        </div>
      </Card>

      {/* ---- references ---- */}
      <RefsCard inp={inp} patch={patch} canEdit={canEdit} busy={busy} uploadRef={uploadRef} onExtract={extract}/>
      {extractRes && (
        <ExtractReviewModal res={extractRes} inp={inp}
          onApply={next => { patch(next); }}
          onClose={() => setExtractRes(null)}/>
      )}

      {/* ---- scope & standing blocks ---- */}
      <Card>
        <div className="p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">4 · Scope & standing controls</div>
          <label className="block text-[12px] text-text-muted">Scope of work
            <Textarea rows={4} value={inp.scope} disabled={!canEdit}
              placeholder="What is being done, to what structure, with what — in your words. The document prints this."
              onChange={e => patch({ scope: e.target.value })}/>
          </label>
          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-2 text-[12px] text-text cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={inp.suspended_loads} disabled={!canEdit}
                onChange={e => patch({ suspended_loads: e.target.checked })}/>
              <span>Working under suspended loads — prints the standing controls block:
                <span className="block text-[11px] text-text-muted mt-0.5">{SUSPENDED_LOADS_WORDING[0]} (+{SUSPENDED_LOADS_WORDING.length - 1} more)</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-[12px] text-text cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={inp.painted_structure} disabled={!canEdit}
                onChange={e => patch({ painted_structure: e.target.checked })}/>
              <span>Painted structure — prints the soft-slings-only rule:
                <span className="block text-[11px] text-text-muted mt-0.5">{PAINTED_STRUCTURE_WORDING}</span>
              </span>
            </label>
          </div>
          <div className="bg-ink rounded-md border border-line/50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-dim mb-1">Site conditions & crane setup — fixed wording, always printed</div>
            <div className="text-[12px] text-text-muted leading-relaxed">{CRANE_SETUP_WORDING}</div>
          </div>
          <label className="block text-[12px] text-text-muted">Site notes — added AFTER the fixed wording (access, laydown, overheads…)
            <Textarea rows={2} value={inp.site_conditions_extra} disabled={!canEdit}
              onChange={e => patch({ site_conditions_extra: e.target.value })}/>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block text-[12px] text-text-muted">Wind limit (m/s)
              <Input value={inp.environmental.wind_limit_ms ?? ""} disabled={!canEdit}
                placeholder={inp.crane?.chart_wind_ms != null ? `chart says ${inp.crane.chart_wind_ms}` : inp.crane ? "from the lift study" : "from the engineer, the client or Coretel's standing limit"}
                onChange={e => patch({ environmental: { ...inp.environmental, wind_limit_ms: e.target.value ? Number(e.target.value) : null } })}/>
              {canEdit && inp.environmental.wind_limit_ms == null && CORETEL_STANDING_WIND_MS != null && (
                <button type="button" className="mt-1 text-[11px] text-accent hover:underline"
                  onClick={() => patch({ environmental: { ...inp.environmental, wind_limit_ms: CORETEL_STANDING_WIND_MS, notes: [inp.environmental.notes, CORETEL_STANDING_WIND_WORDING].filter(Boolean).join(" ") } })}>
                  Use Coretel's standing limit ({CORETEL_STANDING_WIND_MS} m/s) — our rule, not a standard
                </button>
              )}
            </label>
            <label className="block text-[12px] text-text-muted md:col-span-2">Environmental notes
              <Input value={inp.environmental.notes} disabled={!canEdit}
                placeholder="Weather hold points, ground after rain, daylight window…"
                onChange={e => patch({ environmental: { ...inp.environmental, notes: e.target.value } })}/>
            </label>
          </div>
          {/* Forecast snip — prints under the wind paragraph in Environmental
              limits (Beechmont Rev 3 pattern: the Windy screenshot). */}
          {inp.forecast_image ? (
            <div className="flex items-center gap-2 text-[11px] text-text-muted">
              <FileText size={12}/> forecast snip attached
              {canEdit && (
                <button type="button" className="text-text-dim hover:text-danger"
                  onClick={() => patch({ forecast_image: null })}><X size={12}/></button>
              )}
            </div>
          ) : canEdit && (
            <label className="inline-flex items-center gap-1.5 text-[11px] text-text-dim hover:text-text cursor-pointer">
              <Upload size={12}/> attach a forecast snip for the work date — prints in Environmental limits
              <input type="file" accept="image/*" className="hidden"
                onChange={async e => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  const path = await uploadRef(f, "other");
                  if (path) patch({ forecast_image: path });
                }}/>
            </label>
          )}
        </div>
      </Card>

      {/* ---- document blocks (23 Sep 2026, Projects brief 2 A2) ----
          A rule that governs every step without being one — the communications
          protocol is the case that found it. Several Willowvale steps said
          "per the call protocol" while the protocol itself was nowhere in the
          document the crew was holding, because the builder had no field for
          it and the reader had nowhere to put it. Prints before the method. */}
      <Card>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-wider text-text-muted">4b · Document blocks — rules that govern every step</div>
            {canEdit && (
              <Button size="sm" variant="secondary" icon={Plus}
                onClick={() => patch({ blocks: [...(inp.blocks ?? []), { title: "", body: "" }] })}>Add block</Button>
            )}
          </div>
          <div className="text-[12px] text-text-muted">
            A titled section that prints before the method — the communications protocol, permit or client conditions,
            emergency arrangements. Not a step: nobody ticks it off, it applies to all of them. Leave empty and nothing prints.
          </div>
          {(inp.blocks ?? []).map((b, i) => (
            <div key={i} className="border border-line rounded-md p-3 space-y-2">
              <div className="flex gap-2 items-center">
                <Input className="flex-1" value={b.title} placeholder="Communications — the call protocol" disabled={!canEdit}
                  onChange={e => { const blocks = [...(inp.blocks ?? [])]; blocks[i] = { ...b, title: e.target.value }; patch({ blocks }); }}/>
                {canEdit && (
                  <button type="button" className="text-text-dim hover:text-danger"
                    onClick={() => patch({ blocks: (inp.blocks ?? []).filter((_, x) => x !== i) })}><Trash2 size={13}/></button>
                )}
              </div>
              <Textarea rows={Math.min(14, Math.max(4, b.body.split("\n").length + 1))} value={b.body} disabled={!canEdit}
                placeholder={"Every adjustment call follows the same form:\nCONTROL: “Leg B, up two.”\nLEG B: “Leg B, up two.” — read back before touching anything"}
                onChange={e => { const blocks = [...(inp.blocks ?? [])]; blocks[i] = { ...b, body: e.target.value }; patch({ blocks }); }}/>
            </div>
          ))}
        </div>
      </Card>

      {/* ---- method ---- */}
      <MethodCard inp={inp} patch={patch} canEdit={canEdit} busy={busy} uploadRef={uploadRef} tidy={tidy}/>

      {/* ---- don'ts (21 Sep 2026, Willowvale backbone Appendix B) ---- */}
      <Card>
        <div className="p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">5b · Don'ts — standing rules for this job</div>
          <div className="text-[12px] text-text-muted">One per line. Prints as its own block after the method. Leave empty and nothing prints.</div>
          <Textarea rows={Math.min(8, Math.max(3, (inp.donts ?? []).length + 1))} disabled={!canEdit}
            value={(inp.donts ?? []).join("\n")}
            placeholder="Never leave a come-along holding a guy load as the finished arrangement…"
            onChange={e => patch({ donts: e.target.value.split("\n").map(l => l.replace(/^[-*•]\s+/, "")) })}/>
        </div>
      </Card>

      {/* ---- still open (21 Sep 2026, Projects brief D5) ---- */}
      <Card>
        <div className="p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">5c · Still open — what this MOP does not yet settle</div>
          <div className="text-[12px] text-text-muted">One per line. Prints as a STILL OPEN panel on every copy while anything is listed — a draft says what it doesn't know. Delete a line when it closes.</div>
          <Textarea rows={Math.min(8, Math.max(3, (inp.open_items ?? []).length + 1))} disabled={!canEdit}
            value={(inp.open_items ?? []).join("\n")}
            placeholder="Working wind limit and its source — Waveconn / director"
            onChange={e => patch({ open_items: e.target.value.split("\n").map(l => l.replace(/^[-*•]\s+/, "")) })}/>
        </div>
      </Card>

      {/* ---- before you generate ---- */}
      <div ref={flagsCardRef}>
      <Card>
        <div className="p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">6 · Before you generate — what still blocks the document</div>
          {/* Lifting points only exist when something is lifted. No crane on the
              MOP → the row reads N/A and the flag never fires (21 Sep 2026,
              James: "not all MOPs have a lift"). */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-[12px] text-text-muted">Lifting points source — never assumed
              <Select value={inp.crane ? inp.flags.lifting_points_source : "na"} disabled={!canEdit || !inp.crane}
                onChange={e => patch({ flags: { ...inp.flags, lifting_points_source: (e.target.value === "na" ? "" : e.target.value) as any } })}>
                {!inp.crane && <option value="na">N/A — no lift on this MOP</option>}
                <option value="">Not set</option>
                <option value="lift_study">Stated in the lift study</option>
                <option value="erection_drawings">Stated in the erection drawings</option>
                <option value="project_input">Project input — engineered / directed</option>
              </Select>
            </label>
            <label className="block text-[12px] text-text-muted">Lifting points detail
              <Input value={inp.crane ? inp.flags.lifting_points_detail : ""} disabled={!canEdit || !inp.crane}
                placeholder={inp.crane ? "e.g. 2-leg sling to designed lugs per drawing LB-40-E3" : "N/A — there is no lift on this job"}
                onChange={e => patch({ flags: { ...inp.flags, lifting_points_detail: e.target.value } })}/>
            </label>
          </div>
          {flags.length === 0 && (
            <div className="text-[12px] text-ok flex items-center gap-1.5"><ShieldCheck size={13}/> Nothing outstanding — good to generate.</div>
          )}
          {blocking.map(f => (
            <div key={f.id} className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0"/><span>{f.text}</span>
            </div>
          ))}
          {advisories.map(f => (
            <label key={f.id} className="flex items-start gap-2 bg-warn/10 border border-warn/30 rounded px-3 py-2 text-[12px] text-warn cursor-pointer">
              <input type="checkbox" className="mt-0.5" disabled={!canEdit}
                checked={inp.flags.acknowledged.includes(f.id)}
                onChange={e => patch({ flags: { ...inp.flags, acknowledged: e.target.checked
                  ? [...inp.flags.acknowledged, f.id]
                  : inp.flags.acknowledged.filter(x => x !== f.id) } })}/>
              <span>{f.text}</span>
            </label>
          ))}
        </div>
      </Card>
      </div>

      {/* ---- delete draft ---- */}
      {/* Only for a draft that has never been approved (version 0, no frozen
          revisions). Once a director has approved even once, the MOP carries
          history and stays forever — supersede it with a new revision instead. */}
      {canEdit && row.status === "draft" && (row.version ?? 0) === 0 && (
        <div className="pt-1 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11px] text-text-dim">
            False start, or testing? A never-approved draft can be removed so the register stays honest.
          </span>
          <Button size="sm" variant="danger" icon={Trash2} onClick={() => void deleteDraft()} disabled={busy != null}>
            {busy === "delete" ? <Loader2 size={14} className="animate-spin"/> : "Delete draft"}
          </Button>
        </div>
      )}

      {/* ---- save / approve bar ---- */}
      {canEdit && (
        <div className="fixed bottom-0 left-0 right-0 bg-ink-card/95 backdrop-blur border-t border-line z-40">
          <div className="max-w-[1000px] mx-auto p-3 flex items-center gap-3 flex-wrap">
            <div className="text-[12px] text-text-muted flex-1 min-w-[200px] flex items-center gap-1 flex-wrap">
              <span>{dirty ? (approved || row.status === "approved" ? "Unsaved changes" : "Autosaving\u2026") : saved ? "Saved" : approved ? `Approved v${row.version}` : "Draft \u2014 saves automatically"}</span>
              {(blocking.length > 0 || unacked.length > 0) && (
                <button type="button"
                  onClick={() => flagsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  className="underline decoration-dotted underline-offset-2">
                  {blocking.length > 0 && <span className="text-danger">{blocking.length} blocking</span>}
                  {blocking.length > 0 && unacked.length > 0 && <span> · </span>}
                  {unacked.length > 0 && <span className="text-warn">{unacked.length} to acknowledge</span>}
                  <span className="text-text-dim"> — show me</span>
                </button>
              )}
            </div>
            <Button variant="secondary" onClick={() => void save()} disabled={busy != null || !dirty}>
              {busy === "save" ? <Loader2 size={14} className="animate-spin"/> : "Save draft"}
            </Button>
            {isDirector && !approved && (
              <Button icon={ShieldCheck} onClick={() => void approve()} disabled={busy != null || blocking.length > 0}>
                {busy === "approve" ? <Loader2 size={14} className="animate-spin"/> : `Approve MOP v${(row.version ?? 0) + 1}`}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// References card
// -----------------------------------------------------------------------------
function RefsCard({ inp, patch, canEdit, busy, uploadRef, onExtract }: {
  inp: MopInputs; patch: (n: Partial<MopInputs>) => void; canEdit: boolean;
  busy: string | null;
  uploadRef: (f: File, kind: MopRefKind) => Promise<string | null>;
  onExtract: (ref: { path: string; file_name: string; kind: string }) => Promise<void>;
}) {
  const [kind, setKind] = useState<MopRefKind>("lift_study");
  const [desc, setDesc] = useState("");

  async function onPick(f: File | null | undefined) {
    if (!f) return;
    const path = await uploadRef(f, kind);
    if (!path) return;
    const ref = { path, file_name: f.name, description: desc.trim(), kind };
    const next: Partial<MopInputs> = { refs: [...inp.refs, ref] };
    // A layout-kind upload doubles as THE layout image for the document.
    if (kind === "layout_image") next.layout_image = path;
    patch(next);
    setDesc("");
  }

  return (
    <Card>
      <div className="p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">3 · Reference documents — attach, then extract</div>
        <div className="text-[12px] text-text-muted">
          The lift study is <b className="text-text">required</b> for any crane MOP. After attaching a document,
          hit <b className="text-text">extract into this MOP</b> next to it — it reads the file and offers to fill
          the fields for you, each value with the quote it came from.
        </div>
        {inp.refs.length > 0 && (
          <div className="divide-y divide-line/40">
            {inp.refs.map((r, i) => (
              <div key={i} className="py-2 flex items-center gap-3 flex-wrap">
                <FileText size={13} className="text-accent flex-shrink-0"/>
                <span className="text-[12px] text-text flex-1 min-w-[160px] truncate">{r.file_name}</span>
                <Pill label={r.kind.replace(/_/g, " ")} tone={r.kind === "lift_study" ? "ok" : "default"} small/>
                {r.description && <span className="text-[11px] text-text-muted truncate max-w-[240px]">{r.description}</span>}
                {canEdit && (
                  <button type="button" disabled={busy === "extract"}
                    onClick={() => void onExtract(r)}
                    className="text-[11px] text-accent hover:underline flex items-center gap-1 disabled:opacity-50">
                    {busy === "extract" ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                    extract into this MOP
                  </button>
                )}
                {canEdit && (
                  <button type="button" className="text-text-dim hover:text-danger"
                    onClick={() => {
                      const next: Partial<MopInputs> = { refs: inp.refs.filter((_, x) => x !== i) };
                      if (inp.layout_image === r.path) next.layout_image = null;
                      patch(next);
                    }}><Trash2 size={13}/></button>
                )}
              </div>
            ))}
          </div>
        )}
        {canEdit && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <label className="block text-[12px] text-text-muted">Type
              <Select value={kind} onChange={e => setKind(e.target.value as MopRefKind)}>
                <option value="lift_study">Lift study</option>
                <option value="erection_drawings">Erection drawings</option>
                <option value="layout_image">Site layout image (overlay snip)</option>
                <option value="other">Other reference</option>
              </Select>
            </label>
            <label className="block text-[12px] text-text-muted">Short description
              <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Borger stand lift study, 12/09"/>
            </label>
            <label className={`flex items-center justify-center gap-2 rounded-md border border-line bg-ink px-3 py-2
                              text-[12px] text-text cursor-pointer hover:border-accent/50 transition ${busy === "upload" ? "opacity-60" : ""}`}>
              {busy === "upload" ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>}
              Attach file
              <input type="file" className="hidden" disabled={busy === "upload"}
                onChange={e => { void onPick(e.target.files?.[0]); e.target.value = ""; }}/>
            </label>
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Method card — ordered steps, user-authored
// -----------------------------------------------------------------------------
function MethodCard({ inp, patch, canEdit, busy, uploadRef, tidy }: {
  inp: MopInputs; patch: (n: Partial<MopInputs>) => void; canEdit: boolean;
  busy: string | null;
  uploadRef: (f: File, kind: MopRefKind) => Promise<string | null>;
  tidy: () => Promise<void>;
}) {
  const steps = inp.method;
  const setStep = (i: number, next: Partial<(typeof steps)[number]>) => {
    const method = [...steps]; method[i] = { ...method[i], ...next }; patch({ method });
  };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d; if (j < 0 || j >= steps.length) return;
    const method = [...steps]; [method[i], method[j]] = [method[j], method[i]]; patch({ method });
  };

  return (
    <Card>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">5 · Method — your steps, in order</div>
          {canEdit && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" icon={Sparkles} disabled={busy != null}
                onClick={() => void tidy()}>
                {busy === "tidy" ? <Loader2 size={13} className="animate-spin"/> : "Tidy the wording"}
              </Button>
              <Button size="sm" variant="secondary" icon={Plus}
                onClick={() => patch({ method: [...steps, { title: "", action: "", details: "" }] })}>Add step</Button>
            </div>
          )}
        </div>
        <div className="text-[12px] text-text-muted">
          Say what happens in your own words — the details field is yours. Language gets tidied at
          generation; <b className="text-text">no technical fact is ever added</b> that you didn't put here.
        </div>
        {steps.map((s, i) => (
          <div key={i} className="border border-line/60 rounded-md p-3 space-y-2 bg-ink">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[12px] text-accent w-[54px]">Step {i + 1}</span>
              <Input className="flex-1" value={s.title} placeholder="Step title — e.g. Stand bottom section" disabled={!canEdit}
                onChange={e => setStep(i, { title: e.target.value })}/>
              {canEdit && (
                <div className="flex gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                    className="text-text-dim hover:text-text disabled:opacity-30"><ChevronUp size={14}/></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === steps.length - 1}
                    className="text-text-dim hover:text-text disabled:opacity-30"><ChevronDown size={14}/></button>
                  <button type="button" className="text-text-dim hover:text-danger ml-1"
                    onClick={() => { if (window.confirm(`Remove step ${i + 1}?`)) patch({ method: steps.filter((_, x) => x !== i) }); }}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              )}
            </div>
            <Input value={s.action} placeholder={s.hold ? "What must be true before the method continues" : "Action — one line on what happens"} disabled={!canEdit}
              onChange={e => setStep(i, { action: e.target.value })}/>
            <label className="flex items-center gap-2 text-[11px] text-text-muted cursor-pointer">
              <input type="checkbox" checked={!!s.hold} disabled={!canEdit} onChange={e => setStep(i, { hold: e.target.checked })}/>
              <span><b className={s.hold ? "text-danger" : "text-text"}>Hold point</b> — the method stops here; prints boxed with who cleared it and when</span>
            </label>
            {/* Editing the details discards the tidied version — the document
                must never print wording the manager hasn't reviewed against
                their own current text. */}
            <Textarea rows={2} value={s.details} placeholder="Details — how, who, holds, checks. Your words." disabled={!canEdit}
              onChange={e => setStep(i, { details: e.target.value, details_pro: undefined })}/>
            {s.details_pro && (
              <div className="bg-ok/5 border border-ok/30 rounded px-3 py-2">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[10px] uppercase tracking-wider text-ok">Tidied — the document prints this</span>
                  {canEdit && (
                    <button type="button" className="text-[11px] text-text-dim hover:text-danger"
                      onClick={() => setStep(i, { details_pro: undefined })}>discard</button>
                  )}
                </div>
                <div className="text-[12px] text-text">{s.details_pro}</div>
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              {s.image_path
                ? <span className="text-[11px] text-text-muted inline-flex items-center gap-1.5">
                    <FileText size={11}/> image attached
                    {canEdit && (
                      <button type="button" className="text-text-dim hover:text-danger"
                        onClick={() => setStep(i, { image_path: null })}><X size={11}/></button>
                    )}
                  </span>
                : canEdit && (
                  <label className={`text-[11px] text-text-muted hover:text-text cursor-pointer inline-flex items-center gap-1.5 ${busy === "upload" ? "opacity-60" : ""}`}>
                    <Upload size={11}/> attach a snip to this step
                    <input type="file" accept="image/*" className="hidden" disabled={busy === "upload"}
                      onChange={async e => {
                        const f = e.target.files?.[0]; e.target.value = "";
                        if (!f) return;
                        const path = await uploadRef(f, "other");
                        if (path) setStep(i, { image_path: path });
                      }}/>
                  </label>
                )}
            </div>
          </div>
        ))}
        {steps.length === 0 && <div className="text-[12px] text-text-dim italic">No steps — add the first one.</div>}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Extract review — proposals from a document, accepted field by field
// -----------------------------------------------------------------------------
// The safety property lives HERE: the worker can only propose; this modal is
// the single door into the MOP's inputs, every proposal shows its source
// quote beside the current value, and only ticked items apply. Crane facts
// and weights are display-only cross-checks — the crane always comes from
// the library, never from a document fill.
function ExtractReviewModal({ res, inp, onApply, onClose }: {
  res: { proposals: any; file_name: string; kind?: string };
  inp: MopInputs;
  onApply: (next: Partial<MopInputs>) => void;
  onClose: () => void;
}) {
  const P = res.proposals ?? {};
  const isBackboneDoc = !!P.backbone;
  const ids: string[] = [];
  if (P.work_date) ids.push("date");
  if (P.crane_none) ids.push("cranenone");
  if (P.suspended_loads) ids.push("wusl");
  if (P.painted_structure) ids.push("painted");
  if (P.site_notes) ids.push("sitenotes");
  if (P.environmental_notes) ids.push("envnotes");
  if (P.donts?.length) ids.push("donts");
  if (P.backbone && P.to_confirm?.length) ids.push("open");
  if (P.wind_limit_ms) ids.push("wind");
  if (P.scope) ids.push("scope");
  if (P.client_chain) ids.push("chain");
  if (P.ground_requirements) ids.push("ground");
  if (P.rigging) ids.push("rigging");
  if (P.lifting_points) ids.push("liftpts");
  (P.crew ?? []).forEach((_: any, i: number) => ids.push(`crew${i}`));
  (P.plant ?? []).forEach((_: any, i: number) => ids.push(`plant${i}`));
  (P.steps ?? []).forEach((_: any, i: number) => ids.push(`step${i}`));
  (P.blocks ?? []).forEach((_: any, i: number) => ids.push(`block${i}`));
  const [checked, setChecked] = useState<Set<string>>(() => new Set(ids));
  // Backbone says "replace all four existing steps" when the builder still
  // holds the crane-lift seed; the manager can flip it to append instead.
  const [replaceSteps, setReplaceSteps] = useState<boolean>(!!P.replace_steps);
  const toggle = (id: string) => setChecked(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  function applySelected() {
    const srcLabel = res.kind === "lift_study" ? "the lift plan"
      : res.kind === "erection_drawings" ? "the erection drawings" : res.file_name;
    const meta = { ...inp.meta, crew: [...inp.meta.crew] };
    const environmental = { ...inp.environmental };
    const plant = [...inp.plant];
    const method = [...inp.method];
    const flags = { ...inp.flags };
    let scope = inp.scope;
    let sce = inp.site_conditions_extra;
    const extra: Partial<MopInputs> = {};
    if (checked.has("date") && P.work_date) meta.mop_date = P.work_date.value;
    if (checked.has("cranenone") && P.crane_none) extra.crane = null;
    if (checked.has("wusl") && P.suspended_loads) extra.suspended_loads = P.suspended_loads.value;
    if (checked.has("painted") && P.painted_structure) extra.painted_structure = P.painted_structure.value;
    if (checked.has("sitenotes") && P.site_notes) sce = P.site_notes.value;
    if (checked.has("envnotes") && P.environmental_notes) environmental.notes = P.environmental_notes.value;
    if (checked.has("donts") && P.donts?.length) extra.donts = P.donts.map((d: any) => d.text);
    if (checked.has("wind") && P.wind_limit_ms) environmental.wind_limit_ms = P.wind_limit_ms.value;
    if (checked.has("scope") && P.scope) scope = P.scope.value;
    if (checked.has("chain") && P.client_chain) meta.client_chain = P.client_chain.value;
    // B1 (Projects brief): a proposal with no value never prints "undefined"
    if (checked.has("ground") && P.ground_requirements?.value) {
      sce = (sce.trim() ? sce + "\n" : "") + `Per ${srcLabel}: ${P.ground_requirements.value}`;
    }
    if (checked.has("rigging") && P.rigging?.value) {
      plant.push({ item: "Rigging arrangement", details: `${P.rigging.value} (per ${srcLabel})` });
    }
    if (checked.has("open") && P.to_confirm?.length) extra.open_items = P.to_confirm.map((t: any) => t.note);
    if (checked.has("liftpts") && P.lifting_points) {
      flags.lifting_points_detail = P.lifting_points.value;
      flags.lifting_points_source = res.kind === "lift_study" ? "lift_study"
        : res.kind === "erection_drawings" ? "erection_drawings" : flags.lifting_points_source || "project_input";
    }
    (P.crew ?? []).forEach((cw: any, i: number) => {
      if (checked.has(`crew${i}`) && cw?.name_role && !meta.crew.includes(cw.name_role)) meta.crew.push(cw.name_role);
    });
    // Same rule for plant — a second extract of the same file used to add the
    // whole rigging list again.
    const havePlant = new Set(plant.map(p => `${p.item}|${p.details}`.trim().toLowerCase()));
    (P.plant ?? []).forEach((pl: any, i: number) => {
      if (!checked.has(`plant${i}`)) return;
      const row = { item: pl.item ?? "", details: pl.details ?? "" };
      const key = `${row.item}|${row.details}`.trim().toLowerCase();
      if (havePlant.has(key)) return;
      havePlant.add(key);
      plant.push(row);
    });
    // Document blocks (brief 2 A2) — matched on title so re-extracting an
    // updated file replaces the block rather than stacking a second copy.
    if ((P.blocks ?? []).length) {
      const blocks = [...(inp.blocks ?? [])];
      (P.blocks ?? []).forEach((bl: any, i: number) => {
        if (!checked.has(`block${i}`)) return;
        // Matched on the normalised title, so "Temporary rig" from an older
        // extract and "The temporary rig" from a newer one are the same block.
        const at = blocks.findIndex(x => blockKey(x.title) === blockKey(bl.title));
        const row = { title: bl.title ?? "", body: bl.body ?? "" };
        if (at >= 0) blocks[at] = row; else blocks.push(row);
      });
      extra.blocks = blocks;
    }

    const anyStep = (P.steps ?? []).some((_: any, i: number) => checked.has(`step${i}`));
    if (anyStep && replaceSteps) method.length = 0;
    // James, 23 Sep 2026: "double loading the same file and using the worker
    // seemed to be doubling up a lot of things". Extracting the same document
    // twice used to append a second copy of every step. A step whose title is
    // already on the MOP is now left alone — re-extracting an updated file
    // adds what is new instead of duplicating what is there. Ticking
    // "replace" still rebuilds the method from the document.
    const haveTitles = new Set(method.map(m => (m.title ?? "").trim().toLowerCase()).filter(Boolean));
    (P.steps ?? []).forEach((st: any, i: number) => {
      if (!checked.has(`step${i}`)) return;
      const key = String(st.title ?? "").trim().toLowerCase();
      if (key && haveTitles.has(key)) return;
      if (key) haveTitles.add(key);
      method.push({ title: st.title ?? "", action: st.action ?? "", details: st.details ?? "", hold: !!st.hold });
    });
    onApply({ meta, environmental, plant, method, flags, scope, site_conditions_extra: sce, ...extra });
    onClose();
  }

  const Row = ({ id, label, current, proposed, source }: { id: string; label: string; current: string; proposed: string; source?: string }) => (
    <label className="flex items-start gap-2.5 py-2 cursor-pointer">
      <input type="checkbox" className="mt-1" checked={checked.has(id)} onChange={() => toggle(id)}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-text-muted">{label}</span>
          {current && <span className="text-[11px] text-text-dim line-through truncate max-w-[220px]">{current}</span>}
        </div>
        <div className="text-[13px] text-text">{proposed}</div>
        {source && <div className="text-[11px] text-text-dim mt-0.5">“{source}”</div>}
      </div>
    </label>
  );

  const cmp = (docVal: any, ours: any) =>
    docVal == null || ours == null ? null : String(docVal) === String(ours);

  const nothing = ids.length === 0 && !P.crane_check && !(P.weights?.length) && !(P.to_confirm?.length) && !(P.blocks?.length);
  const stepRows = (P.steps ?? []) as any[];

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[720px] my-6">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">Found in {res.file_name}</div>
          <button onClick={onClose} type="button" className="text-text-muted hover:text-text"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="bg-ink rounded-md border border-line/50 p-3 text-[12px] text-text-muted leading-relaxed">
            Every item below was read from the document, with its source shown. Nothing applies until
            you tick it and hit apply — <b className="text-text">you are the one putting it in the MOP</b>.
          </div>

          {nothing && <div className="text-[13px] text-text-muted italic">Nothing extractable found in this document.</div>}

          {isBackboneDoc && (
            <div className="text-[12px] text-text-muted">
              Read straight from the document{P.title ? <> — <span className="text-text">{P.title}</span></> : null}, section by section
              against the builder's own fields. No AI touched it and nothing was reworded — the wording below is the wording in the file.
            </div>
          )}

          {(P.work_date || P.crane_none || P.suspended_loads || P.painted_structure) && (
            <div className="divide-y divide-line/40">
              {P.work_date && <Row id="date" label="Work date" current={inp.meta.mop_date} proposed={P.work_date.value} source={P.work_date.source}/>}
              {P.crane_none && <Row id="cranenone" label="Crane" current={inp.crane ? inp.crane.model_label : ""} proposed="No crane on this MOP" source={P.crane_none.source}/>}
              {P.suspended_loads && <Row id="wusl" label="Working under suspended loads block" current={inp.suspended_loads ? "on" : "off"} proposed={P.suspended_loads.value ? "on" : "off"} source={P.suspended_loads.source}/>}
              {P.painted_structure && <Row id="painted" label="Painted structure — soft slings block" current={inp.painted_structure ? "on" : "off"} proposed={P.painted_structure.value ? "on" : "off"} source={P.painted_structure.source}/>}
            </div>
          )}

          {(P.wind_limit_ms || P.scope || P.client_chain || P.ground_requirements || P.rigging || P.lifting_points || P.site_notes || P.environmental_notes) && (
            <div className="divide-y divide-line/40">
              {P.site_notes && <Row id="sitenotes" label="Site notes (replaces current)" current={inp.site_conditions_extra} proposed={P.site_notes.value} source={P.site_notes.source}/>}
              {P.environmental_notes && <Row id="envnotes" label="Environmental notes (replaces current)" current={inp.environmental.notes} proposed={P.environmental_notes.value} source={P.environmental_notes.source}/>}
              {P.wind_limit_ms && <Row id="wind" label="Wind limit (m/s)"
                current={inp.environmental.wind_limit_ms != null ? String(inp.environmental.wind_limit_ms) : ""}
                proposed={String(P.wind_limit_ms.value)} source={P.wind_limit_ms.source}/>}
              {P.scope && <Row id="scope" label="Scope (replaces current)" current={inp.scope} proposed={P.scope.value} source={P.scope.source}/>}
              {P.client_chain && <Row id="chain" label="Client chain" current={inp.meta.client_chain} proposed={P.client_chain.value} source={P.client_chain.source}/>}
              {P.ground_requirements && <Row id="ground" label="Ground requirements (adds to site notes)" current="" proposed={P.ground_requirements.value} source={P.ground_requirements.source}/>}
              {P.rigging && <Row id="rigging" label="Rigging (adds as plant line)" current="" proposed={P.rigging.value} source={P.rigging.source}/>}
              {P.lifting_points && <Row id="liftpts" label="Lifting points (fills Before-you-generate)" current={inp.flags.lifting_points_detail} proposed={P.lifting_points.value} source={P.lifting_points.source}/>}
            </div>
          )}

          {(P.crew?.length > 0 || P.plant?.length > 0) && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1">Crew & plant — added to the lists</div>
              <div className="divide-y divide-line/40">
                {(P.crew ?? []).map((cw: any, i: number) => (
                  <Row key={`c${i}`} id={`crew${i}`} label="Crew" current="" proposed={cw.name_role} source={cw.source}/>
                ))}
                {(P.plant ?? []).map((pl: any, i: number) => (
                  <Row key={`p${i}`} id={`plant${i}`} label="Plant" current="" proposed={`${pl.item} — ${pl.details}`} source={pl.source}/>
                ))}
              </div>
            </div>
          )}

          {P.donts?.length > 0 && (
            <div>
              <Row id="donts" label={`Don'ts — ${P.donts.length} standing rule${P.donts.length === 1 ? "" : "s"} (replaces current)`} current={(inp.donts ?? []).length ? `${(inp.donts ?? []).length} on the MOP` : ""}
                proposed={P.donts.map((d: any) => d.text).join(" · ")} source={P.donts[0].source}/>
            </div>
          )}

          {(P.blocks ?? []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1">
                Document blocks — print before the method, not as steps
              </div>
              <div className="divide-y divide-line/40">
                {(P.blocks ?? []).map((bl: any, i: number) => (
                  <Row key={`b${i}`} id={`block${i}`} label={bl.title || "untitled block"}
                    current={(inp.blocks ?? []).some(x => blockKey(x.title) === blockKey(bl.title)) ? "already on the MOP — replaces it" : ""}
                    proposed={String(bl.body ?? "").split("\n").filter(Boolean).slice(0, 3).join(" · ")} source={bl.source}/>
                ))}
              </div>
            </div>
          )}

          {stepRows.length > 0 && (
            <div>
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <div className="text-[11px] uppercase tracking-wider text-text-muted">Method steps — {replaceSteps ? "REPLACE your existing steps" : "appended after your existing steps"}</div>
                {inp.method.length > 0 && (
                  <label className="text-[11px] text-text-muted flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={replaceSteps} onChange={e => setReplaceSteps(e.target.checked)}/>
                    Replace the {inp.method.length} step{inp.method.length === 1 ? "" : "s"} already on the MOP
                  </label>
                )}
              </div>
              <div className="divide-y divide-line/40">
                {(P.steps ?? []).map((st: any, i: number) => (
                  <Row key={`s${i}`} id={`step${i}`} label={`Step — ${st.title || "untitled"}`} current=""
                    proposed={[st.action, st.details].filter(Boolean).join(" · ")} source={st.source}/>
                ))}
              </div>
            </div>
          )}

          {P.crane_check && (
            <div className="bg-ink rounded-md border border-line/50 p-3">
              <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Crane cross-check — display only (the crane always comes from the library)</div>
              <div className="space-y-1 text-[12px]">
                {P.crane_check.model && <div className="text-text">Document: {P.crane_check.model}{inp.crane ? <> · Selected: {inp.crane.model_label}</> : null}</div>}
                {P.crane_check.counterweight_t != null && (
                  <div className="flex items-center gap-2">
                    <span className="text-text">Counterweight {P.crane_check.counterweight_t} t</span>
                    {inp.crane && cmp(P.crane_check.counterweight_t, inp.crane.counterweight_t) === false
                      ? <Pill label={`MISMATCH — config says ${inp.crane.counterweight_t} t`} tone="critical" small/>
                      : inp.crane ? <Pill label="matches config" tone="ok" small/> : null}
                  </div>
                )}
                {P.crane_check.outriggers && <div className="text-text-muted">Outriggers: {P.crane_check.outriggers}{inp.crane?.outriggers ? ` · config: ${inp.crane.outriggers}` : ""}</div>}
                {P.crane_check.boom_or_radius && <div className="text-text-muted">{P.crane_check.boom_or_radius}</div>}
                <div className="text-[11px] text-text-dim">“{P.crane_check.source}”</div>
              </div>
            </div>
          )}

          {P.weights?.length > 0 && (
            <div className="bg-ink rounded-md border border-line/50 p-3">
              <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Weights stated — for your check against the chart</div>
              {(P.weights ?? []).map((w: any, i: number) => (
                <div key={i} className="text-[12px] text-text">{w.item}: <span className="font-mono">{Number(w.kg).toLocaleString()} kg</span> <span className="text-text-dim">— “{w.source}”</span></div>
              ))}
            </div>
          )}

          {P.to_confirm?.length > 0 && (
            <div className="bg-warn/10 border border-warn/30 rounded-md p-3">
              {isBackboneDoc && (
                <label className="flex items-center gap-2 text-[12px] text-warn mb-2 cursor-pointer">
                  <input type="checkbox" checked={checked.has("open")} onChange={() => toggle("open")}/>
                  Put these on the MOP as its STILL OPEN list (replaces current) — they print until closed
                </label>
              )}
              <div className="text-[11px] uppercase tracking-wider text-warn mb-1.5">The document itself says these are not confirmed</div>
              {(P.to_confirm ?? []).map((t2: any, i: number) => (
                <div key={i} className="text-[12px] text-warn">{t2.note} <span className="opacity-70">— “{t2.source}”</span></div>
              ))}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button onClick={applySelected} disabled={checked.size === 0} className="flex-1 justify-center">
              Apply {checked.size} selected
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
